import React, { useMemo } from 'react';
import { X } from 'lucide-react';
import BrandDropdown from 'shared/components/ui/BrandDropdown';
import FilterValueEditor, {
  getOperatorsForType, isValuelessOp,
} from 'shared/components/filters/FilterValueEditor';

/**
 * InlineFilterChip — operator + value editable directly on the page.
 *
 * Filter shape: `{ field, dataType, operator, value, value2?, sampleValues?,
 * source, category, connector?, _justAdded? }`. The chip renders the field
 * name verbatim (so callers should massage long paths like `span.foo`
 * upstream if a friendlier label is desired).
 *
 * `leading` replaces the WHERE / AND label when given — a caller that lets
 * people choose AND or OR renders its own control there; `null` renders
 * nothing. `wrap` keeps the chip inside a narrow panel: the value shrinks
 * and truncates instead of pushing the panel wider (opt-in: the list pages
 * have the room).
 * `displayField` is shown in place of the field when given (the
 * field is still what values are fetched for, and what the hover shows) —
 * a prop, not a key on `filter`, because filter objects elsewhere are built
 * by spreading a picked property, which already carries a `label`. Left
 * out, the chip behaves exactly as it always has.
 */
export default function InlineFilterChip({ filter, isFirst, onUpdate, onRemove, teamId, agentName, leading, displayField, wrap = false }) {
  const connector = isFirst ? 'WHERE' : (filter.connector || 'AND');
  // If the caller pre-computed a server-driven operator list (e.g. eval
  // filters source operators from /api/evals/schema operator_defs), use it.
  // Otherwise fall back to the hard-coded catalog for the runs/events pages.
  const ops = useMemo(
    () => (Array.isArray(filter.operators) && filter.operators.length
      ? filter.operators
      : getOperatorsForType(filter.dataType)),
    [filter.dataType, filter.operators],
  );

  const onOpChange = (nextOp) => {
    const next = { operator: nextOp };
    if (isValuelessOp(nextOp)) { next.value = undefined; next.value2 = undefined; }
    onUpdate(next);
  };

  return (
    <div className={`flex flex-wrap items-center gap-2 ${wrap ? 'min-w-0 max-w-full' : ''}`}>
      {leading !== undefined ? leading : <span className="w-10 px-0.5 text-[11px] text-[color:var(--text-muted)]">{connector.toLowerCase()}</span>}
      <div
        className={`trodo-chip inline-flex items-center gap-1 rounded-lg border border-[color:var(--border-default)] bg-surface py-0.5 pl-2.5 pr-1 ${wrap ? 'min-w-0 max-w-full' : ''}`}
      >
        <span className="font-mono text-[12px] font-medium text-[color:var(--text-primary)]" title={displayField ? filter.field : undefined}>{displayField ?? filter.field}</span>
        <div style={{ minWidth: 96, width: 'max-content' }}>
          <BrandDropdown
            value={filter.operator}
            onChange={onOpChange}
            options={ops}
            compact
            menuMinWidth={220}
          />
        </div>
        {wrap ? (
          <div className="min-w-0 shrink overflow-hidden">
            <FilterValueEditor filter={filter} onUpdate={onUpdate} teamId={teamId} agentName={agentName} />
          </div>
        ) : (
          <FilterValueEditor
            filter={filter}
            onUpdate={onUpdate}
            teamId={teamId}
            agentName={agentName}
          />
        )}
        {/* Inside a chip the operator and value read as words in one line, not boxes in a box. */}
        <style>{`
          .trodo-chip .brand-dropdown-trigger, .trodo-chip [data-chip-control] { border-color: transparent !important; background: transparent !important; height: 26px; }
          .trodo-chip .brand-dropdown-trigger:hover, .trodo-chip [data-chip-control]:hover { background: var(--surface-hover) !important; }
        `}</style>
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-[color:var(--text-muted)] transition-colors hover:bg-surface-hover hover:text-[color:var(--text-primary)]"
          title="Remove filter"
          aria-label="Remove filter"
        >
          <X size={13} />
        </button>
      </div>
    </div>
  );
}
